import React, { useState, useRef } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog'
import { Button } from '../ui/button'
import { AlertCircle, CheckCircle, Download, Upload, X, FileText } from 'lucide-react'
import { Card } from '../ui/card'
import { BulkUploadSuccessModal } from './bulk-uploa-success-modal'
import { addBulkRewardPoints, createBulkRewardRequest } from '../../api/rewards'
import { countWords } from '../../lib/utils'

interface BulkUploadModalProps {
  isOpen: boolean
  onClose: () => void
  onBulkSuccess: (
    transactions: {
      firstName: string
      lastName: string
      email: string
      employeeEntrataEmail?: string
      points: number
      description: string
      rewardedBy: string
      department?: string
    }[]
  ) => void
  onNavigateToHistory: () => void
}

interface ParsedRecord {
  firstName: string
  lastName: string
  email: string
  department: string
  employeeEntrataEmail?: string
  points: number
  description: string
}

const REQUIRED_HEADERS = [
  'First Name',
  'Last Name',
  'Email id',
  'Department Funding Points',
  'Points',
  'Description',
]

const EXPECTED_HEADERS_WITH_EMPLOYEE_EMAIL = [
  'First Name',
  'Last Name',
  'Email id',
  'Department Funding Points',
  'Employee Entrata Email Address',
  'Points',
  'Description',
]

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const parseCSVLine = (text: string): string[] => {
  const result: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    if (char === '"') {
      inQuotes = !inQuotes
    }

    if (char === ',' && !inQuotes) {
      result.push(current)
      current = ''
      continue
    }

    current += char
  }
  result.push(current)

  return result.map(v => v.trim().replace(/"/g, ''))
}

export function BulkUploadModal({
  isOpen,
  onClose,
  onBulkSuccess,
  onNavigateToHistory,
}: BulkUploadModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [parsedData, setParsedData] = useState<ParsedRecord[]>([])
  const [errors, setErrors] = useState<string[]>([])
  const [uploadResult, setUploadResult] = useState<{
    type: 'success' | 'error'
    message: string
  } | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successfulRecordsCount, setSuccessfulRecordsCount] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const validateEmail = (email: string): boolean => EMAIL_REGEX.test(email)

  const parseCSV = (content: string): { data: ParsedRecord[]; errors: string[] } => {
    const lines = content.trim().split('\n')
    const errors: string[] = []
    const data: ParsedRecord[] = []

    if (lines.length === 0) {
      errors.push('CSV file is empty')
      return { data: [], errors }
    }

    const actualHeaders = parseCSVLine(lines[0])
    const hasEmployeeEmailColumn = actualHeaders.includes('Employee Entrata Email Address')
    const expectedHeaders = hasEmployeeEmailColumn
      ? EXPECTED_HEADERS_WITH_EMPLOYEE_EMAIL
      : REQUIRED_HEADERS
    const expectedColumnCount = expectedHeaders.length

    // Check if headers match exactly in the correct order
    if (actualHeaders.length !== expectedColumnCount) {
      const errorMessage = `Invalid CSV header.|Expected exactly ${expectedColumnCount} columns in order: ${expectedHeaders.join(', ')}.|Got ${actualHeaders.length} columns: ${actualHeaders.join(', ')}`
      errors.push(errorMessage)
      return { data: [], errors }
    }

    for (let i = 0; i < expectedHeaders.length; i++) {
      if (actualHeaders[i] !== expectedHeaders[i]) {
        const errorMessage = `Invalid CSV header order.|Expected headers in exact order: ${expectedHeaders.join(', ')}.|Headers must be in the specified order.`
        errors.push(errorMessage)
        return { data: [], errors }
      }
    }

    const firstNameIndex = 0
    const lastNameIndex = 1
    const emailIndex = 2
    const departmentIndex = 3
    const employeeEmailIndex = hasEmployeeEmailColumn ? 4 : -1
    const pointsIndex = hasEmployeeEmailColumn ? 5 : 4
    const descriptionIndex = hasEmployeeEmailColumn ? 6 : 5

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim()
      if (!line) continue

      const values = parseCSVLine(line)

      if (values.length !== expectedColumnCount) {
        errors.push(`Row ${i + 1}: Expected ${expectedColumnCount} columns, got ${values.length}`)
        continue
      }

      const firstName = values[firstNameIndex]
      const lastName = values[lastNameIndex]
      const email = values[emailIndex]
      const department = values[departmentIndex]
      const employeeEntrataEmail = hasEmployeeEmailColumn ? values[employeeEmailIndex] : undefined
      const pointsStr = values[pointsIndex]
      const description = values[descriptionIndex]

      const rowErrors: string[] = []

      if (!firstName?.trim()) rowErrors.push(`Row ${i + 1}: First Name is required`)
      if (!lastName?.trim()) rowErrors.push(`Row ${i + 1}: Last Name is required`)
      if (!email?.trim()) {
        rowErrors.push(`Row ${i + 1}: Email id is required`)
      } else if (!validateEmail(email)) {
        rowErrors.push(`Row ${i + 1}: Invalid email format: ${email}`)
      }
      if (!department?.trim()) rowErrors.push(`Row ${i + 1}: Department Funding Points is required`)

      if (hasEmployeeEmailColumn && employeeEntrataEmail?.trim()) {
        if (!validateEmail(employeeEntrataEmail)) {
          rowErrors.push(
            `Row ${i + 1}: Invalid Employee Entrata Email Address format: ${employeeEntrataEmail}`
          )
        } else if (!employeeEntrataEmail.endsWith('@entrata.com')) {
          rowErrors.push(
            `Row ${i + 1}: Employee Entrata Email Address must be an @entrata.com address: ${employeeEntrataEmail}`
          )
        }
      }

      if (!pointsStr?.trim()) {
        rowErrors.push(`Row ${i + 1}: Points is required`)
      }

      const points = parseInt(pointsStr || '')
      if (pointsStr?.trim() && (isNaN(points) || points <= 0)) {
        rowErrors.push(`Row ${i + 1}: Points must be a positive number, got: ${pointsStr}`)
      }

      if (!description?.trim()) {
        rowErrors.push(`Row ${i + 1}: Description is required`)
      } else if (countWords(description) > 500) {
        rowErrors.push(`Row ${i + 1}: Description must be 500 words or less`)
      }

      if (rowErrors.length > 0) {
        errors.push(...rowErrors)
        continue
      }

      data.push({
        firstName: firstName?.trim() || '',
        lastName: lastName?.trim() || '',
        email: email?.trim() || '',
        department: department?.trim() || '',
        employeeEntrataEmail: employeeEntrataEmail?.trim() || undefined,
        points,
        description: description?.trim() || '',
      })
    }

    if (data.length === 0 && errors.length === 0) {
      errors.push(
        'CSV file contains only headers with no data rows. Please add at least one record to upload.'
      )
    }

    return { data, errors }
  }

  const clearFile = () => {
    setSelectedFile(null)
    setParsedData([])
    setErrors([])
    setUploadResult(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setUploadResult({
        type: 'error',
        message: 'Please select a valid CSV file',
      })
      return
    }

    setSelectedFile(file)
    setUploadResult(null)
    setErrors([])
    setParsedData([])

    const reader = new FileReader()
    reader.onload = e => {
      const content = e.target?.result as string
      const { data, errors } = parseCSV(content)

      setParsedData(data)
      setErrors(errors)

      if (errors.length > 0) {
        setUploadResult({
          type: 'error',
          message: `Found ${errors.length} validation error(s). Please fix and try again.`,
        })
      }
    }

    reader.readAsText(file)
  }

  const downloadTemplate = () => {
    const csvContent = `First Name,Last Name,Email id,Department Funding Points,Employee Entrata Email Address,Points,Description
Alex,Thompson,alex.thompson@example.com,14 - Marketing,alex.thompson@entrata.com,150,Exceptional work on Q4 marketing campaign
Jessica,Martinez,jessica.martinez@example.com,12 - Leasing Center,jessica.martinez@entrata.com,200,Outstanding customer service
Michael,Chen,michael.chen@example.com,4 - Software Development,michael.chen@entrata.com,175,Innovative feature development`

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'bulk_rewards_template.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const handleUpload = async () => {
    if (!selectedFile || parsedData.length === 0) return

    setIsUploading(true)

    try {
      const bulkRequest = createBulkRewardRequest(selectedFile)

      const response = await addBulkRewardPoints(bulkRequest)

      if (response.message && response.processed) {
        const transactions = parsedData.map(record => ({
          firstName: record.firstName,
          lastName: record.lastName,
          email: record.email,
          employeeEntrataEmail: record.employeeEntrataEmail,
          points: record.points,
          description: record.description,
          rewardedBy: 'Admin User',
          department: record.department,
        }))

        setSuccessfulRecordsCount(response.processed)
        onBulkSuccess(transactions)
        setShowSuccessModal(true)
      } else {
        setUploadResult({
          type: 'error',
          message: 'Upload failed. Please try again later.',
        })
      }
    } catch {
      setUploadResult({
        type: 'error',
        message: 'An unexpected error occurred during upload.',
      })
    } finally {
      setIsUploading(false)
    }
  }

  const handleClose = () => {
    if (!isUploading) {
      clearFile()
      onClose()
    }
  }

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false)
    setSuccessfulRecordsCount(0)
    handleClose()
  }

  const handleViewHistory = () => {
    setShowSuccessModal(false)
    setSuccessfulRecordsCount(0)
    onNavigateToHistory()
    handleClose()
  }

  const renderHeaderError = (error: string) => {
    const parts = error.split('|')
    return (
      <div className="space-y-1">
        <div className="text-sm text-red-600 flex items-center gap-2">
          <span className="w-1 h-1 bg-red-600 rounded-full flex-shrink-0"></span>
          <span>{parts[0]}</span>
        </div>
        <div className="ml-4 space-y-1">
          <div className="text-sm text-red-600">1. {parts[1]}</div>
          <div className="text-sm text-red-600">2. {parts[2]}</div>
          <div className="text-sm text-red-600">
            3. <span className="text-base font-medium">{parts[3]}</span>
          </div>
        </div>
      </div>
    )
  }

  const renderRegularError = (error: string, index: number) => (
    <div key={index} className="text-sm text-red-600 flex items-center gap-2">
      <span className="w-1 h-1 bg-red-600 rounded-full flex-shrink-0"></span>
      <span>{error}</span>
    </div>
  )

  return (
    <>
      <Dialog open={isOpen && !showSuccessModal} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Upload Points</DialogTitle>
            <DialogDescription>
              Upload a CSV file to reward points to multiple recipients at once.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <Card className="p-4 border-l-4 border-l-blue-500">
              <div className="flex items-start gap-3">
                <FileText className="h-5 w-5 text-blue-500 mt-0.5" />
                <div>
                  <h4 className="font-medium text-sm mb-2">Required CSV Format</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Your CSV file must contain the following columns in the exact order shown. The
                    Employee Entrata Email Address column is optional:
                  </p>
                  <div className="bg-gray-50 p-3 rounded text-sm font-mono">
                    First Name, Last Name, Email id, Department Funding Points, Employee Entrata
                    Email Address, Points, Description
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="text-xs text-muted-foreground">
                      <strong>Validation Rules:</strong>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>
                          <strong>Headers must be in the exact order shown above</strong>
                        </li>
                        <li>
                          First Name, Last Name, Email id, Department Funding Points, Points, and
                          Description are required
                        </li>
                        <li>Email id must be a valid email format</li>
                        <li>
                          Employee Entrata Email Address must be an @entrata.com address if provided
                        </li>
                        <li>Department Funding Points must be specified for all recipients</li>
                        <li>Points must be a positive number</li>
                      </ul>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={downloadTemplate}
                      className="flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Download Template
                    </Button>
                  </div>
                </div>
              </div>
            </Card>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Select CSV File</label>
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    {selectedFile ? 'Replace file' : 'Choose file'}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {selectedFile && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={clearFile}
                        className="h-6 w-6 p-0 hover:bg-destructive/10 hover:text-destructive"
                        title="Remove file"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
                {!selectedFile && (
                  <p className="text-xs text-muted-foreground mt-1">
                    No file selected. Choose a CSV file to upload. (Max file size: 10MB)
                  </p>
                )}
              </div>
            </div>

            {uploadResult && (
              <div
                className={`flex items-start gap-2 p-3 rounded ${
                  uploadResult.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}
              >
                {uploadResult.type === 'success' ? (
                  <CheckCircle className="h-4 w-4 mt-0.5" />
                ) : (
                  <AlertCircle className="h-4 w-4 mt-0.5" />
                )}
                <span className="text-sm">{uploadResult.message}</span>
              </div>
            )}

            {errors.length > 0 && (
              <Card className="p-4 border-l-4 border-l-red-500">
                <h4 className="font-medium text-sm mb-2 text-red-700">
                  Validation Errors ({errors.length}):
                </h4>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {errors.map((error, index) => {
                    const isHeaderError =
                      error.includes('Invalid CSV header.') && error.includes('Missing:')
                    return isHeaderError
                      ? renderHeaderError(error)
                      : renderRegularError(error, index)
                  })}
                </div>
              </Card>
            )}

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={handleClose} disabled={isUploading}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleUpload}
                disabled={
                  !selectedFile || errors.length > 0 || parsedData.length === 0 || isUploading
                }
                className="bg-[#000000] hover:bg-[#4a4a4a] text-white"
              >
                {isUploading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    <span>Processing...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    <span>Upload & Process ({parsedData.length} records)</span>
                  </div>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <BulkUploadSuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        totalRecords={successfulRecordsCount}
        onViewHistory={handleViewHistory}
      />
    </>
  )
}
